import type { ReactNode } from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { useDroppable } from '@dnd-kit/react';
import { useTranslation } from 'react-i18next';

import { getPluralized } from '@crm/shared/lib';
import { cn, tw } from '@crm/shared/lib/styles/utils';
import { formatPrice } from '@shared/helpers';

import { bucketDropTargetConfigByKey } from '../../config';
import { bucketEmptyTitleKeys, bucketSubTitleKeys } from '../../model/bucket/consts';

import type { TBucket } from '@crm/entities/deals-board';

interface IBoardBoxProps {
    bucketData: TBucket;
    aside?: ReactNode;
    boardContainerClassName?: string;
    boardHeaderTextClassName?: string;
}

export const BoardBox = ({
    bucketData,
    aside,
    boardContainerClassName,
    boardHeaderTextClassName,
}: IBoardBoxProps) => {
    const { t } = useTranslation();
    const { ref, isDropTarget } = useDroppable({
        id: `terminal-bucket-${bucketData?.key}`,
        type: 'terminal-bucket',
        accept: 'deal',
        data: { bucketKey: bucketData?.key },
        collisionPriority: CollisionPriority.Low,
    });
    const formattedPrice = formatPrice(Number(bucketData.totalBudget), {
        currencyCode: bucketData.currency.code,
        currencySign: bucketData.currency.sign,
        withCurrency: true,
    });
    const pluralized = getPluralized(bucketData.count, {
        one: t('crm.common.plurals.deal.one'),
        few: t('crm.common.plurals.deal.few'),
        many: t('crm.common.plurals.deal.many'),
    });

    const boardContainerStyles = tw(
        'relative flex h-full justify-between rounded-[10px] bg-neutral-100 p-4.5'
    );
    const detailsTextStyles = tw('line-clamp-1 text-xs leading-4 text-smtr-main-tertiary-100');
    const isEmpty = bucketData.count === 0;
    const dropTargetConfig = bucketDropTargetConfigByKey[bucketData.key];

    return (
        <li
            ref={ref}
            className={cn(
                boardContainerStyles,
                boardContainerClassName,
                isEmpty && 'justify-center',
                dropTargetConfig.className
            )}
        >
            {isDropTarget && (
                <div className="absolute inset-0 z-0 flex items-center justify-center rounded-xl border border-dashed border-sky-500/50 bg-smtr-main-accent-100/5 px-4 py-3 backdrop-blur-sm">
                    <span className="relative z-10 rounded-4xl bg-white px-4 py-2 text-center text-xs text-smtr-main-black-100 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.10)]">
                        {t(dropTargetConfig.titleKey)}
                    </span>
                </div>
            )}
            {isEmpty ? (
                <div className="flex flex-col gap-1">
                    <h5
                        className={cn(
                            'text-center text-base leading-5 font-bold',
                            boardHeaderTextClassName
                        )}
                    >
                        {t(bucketEmptyTitleKeys[bucketData.key])}
                    </h5>
                    <h6 className={cn(detailsTextStyles, 'flex gap-2.5 text-center')}>
                        {t(bucketSubTitleKeys[bucketData.key])}
                    </h6>
                </div>
            ) : (
                <div className="flex w-full items-center justify-between gap-2.5">
                    <div className="flex flex-col gap-1">
                        <h5
                            className={cn(
                                'text-base leading-5 font-bold',
                                boardHeaderTextClassName
                            )}
                        >
                            {bucketData.title}
                        </h5>
                        <div className="flex gap-2.5">
                            <span className={detailsTextStyles}>{pluralized}</span>
                            <span className={detailsTextStyles}>{formattedPrice}</span>
                        </div>
                    </div>
                    <div>{aside && aside}</div>
                </div>
            )}
        </li>
    );
};
